/*
  Wild Paws and Claws – welcome popup (<wpc-welcome-popup>, sections/wpc-welcome-popup.liquid).
  - opens after data-delay ms, at most once every data-frequency days (localStorage timestamp, try/catch)
  - never on cart/checkout, never once the visitor subscribed (Liquid skips customer.accepts_marketing;
    the "wpc-subscribed" flag is set here and by the homepage newsletter after a successful sign-up)
  - re-opens straight away after its OWN form post (success / error) – a sessionStorage flag set on submit
    stops the newsletter section's post from opening the popup
  - page scroll lock via window.wpcScrollLock (assets/wpc-scroll-lock.js): locked only while visible,
    released on every close path (Esc, overlay, close button, "No thanks", disconnect, pagehide)
  - theme editor: opens when the section is selected, closes when deselected
*/
(() => {
  if (customElements.get('wpc-welcome-popup')) return;

  const store = (area) => ({
    get(key) {
      try { return window[area].getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { window[area].setItem(key, value); } catch (e) { /* private mode / blocked storage */ }
    },
    remove(key) {
      try { window[area].removeItem(key); } catch (e) { /* blocked storage */ }
    },
  });
  const local = store('localStorage');
  const session = store('sessionStorage');
  const SUBSCRIBED_KEY = 'wpc-subscribed';
  const SUBMITTED_KEY = 'wpc-popup-submitted';
  const DAY = 24 * 60 * 60 * 1000;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const lock = (on) => {
    const api = window.wpcScrollLock;
    if (api) api[on ? 'lock' : 'unlock']('wpc-welcome-popup');
  };

  class WpcWelcomePopup extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.storageKey || 'wpc-welcome-popup';
      this.days = Number(this.dataset.frequency || 30);
      this.dialog = this.querySelector('.wpc-popup__dialog');
      this.form = this.closest('form');
      this.onKey = this.onKey.bind(this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-wpc-popup-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      /* Result of this popup's own form post */
      const state = this.dataset.state;
      const ownPost = session.get(SUBMITTED_KEY) === '1';
      if (state && ownPost) {
        session.remove(SUBMITTED_KEY);
        if (state === 'success') {
          local.set(SUBSCRIBED_KEY, '1');
          this.showPanel('success');
        }
        this.open();
        return;
      }
      if (state === 'success') local.set(SUBSCRIBED_KEY, '1'); /* subscribed through another form */

      if (this.hasAttribute('data-design-mode')) return;
      if (/^\/(cart|checkout|checkouts)(\/|$)/.test(window.location.pathname)) return;
      if (local.get(SUBSCRIBED_KEY)) return;
      const last = Number(local.get(this.key));
      if (last && Date.now() - last < this.days * DAY) return;

      const delay = Number(this.dataset.delay || 8000);
      this.timer = window.setTimeout(() => this.maybeOpen(), delay);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKey);
      lock(false);
    }

    /* Don't interrupt someone who is using the cart drawer, search, the menu or another dialog. */
    busy() {
      return Boolean(
        document.querySelector('dialog[open], details.menu-open, theme-drawer[open]') ||
          document.documentElement.hasAttribute('scroll-lock') ||
          document.querySelector('.rps_search_overlay_container, #rapid-search-results.search-results-mobile:not([style*="none"])')?.getClientRects().length
      );
    }

    maybeOpen() {
      if (this.busy()) {
        this.timer = window.setTimeout(() => this.maybeOpen(), 4000);
        return;
      }
      this.open();
    }

    showPanel(name) {
      this.querySelectorAll('[data-wpc-popup-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.wpcPopupPanel !== name;
      });
    }

    open() {
      if (!this.hidden) return;
      window.clearTimeout(this.timer);
      local.set(this.key, String(Date.now()));
      this.previousFocus = document.activeElement;
      this.hidden = false;
      lock(true);
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const target = this.querySelector('[data-wpc-popup-panel]:not([hidden]) input[type="email"]') ||
          this.querySelector('.wpc-popup__close');
        if (target) target.focus({ preventScroll: true });
      });
    }

    close() {
      if (this.hidden) return;
      local.set(this.key, String(Date.now()));
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      /* Unlock straight away – never leave the page locked behind a fading popup. */
      lock(false);
      const finish = () => {
        this.hidden = true;
        if (this.previousFocus && typeof this.previousFocus.focus === 'function') this.previousFocus.focus({ preventScroll: true });
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else window.setTimeout(finish, 220);
    }

    onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    onSubmit(event) {
      const input = this.querySelector('input[type="email"]');
      const message = this.querySelector('[data-wpc-popup-invalid]');
      if (!input || EMAIL.test(input.value.trim())) {
        if (message) message.hidden = true;
        const button = this.querySelector('.wpc-popup__submit');
        if (button) button.setAttribute('aria-busy', 'true');
        session.set(SUBMITTED_KEY, '1');
        return; /* normal form post – page reloads and re-opens the popup with the result */
      }
      event.preventDefault();
      if (message) message.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.focus();
    }
  }

  customElements.define('wpc-welcome-popup', WpcWelcomePopup);

  /* Theme editor: preview the popup while its section is selected. */
  document.addEventListener('shopify:section:select', (e) => {
    const popup = e.target.querySelector && e.target.querySelector('wpc-welcome-popup');
    if (popup) popup.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const popup = e.target.querySelector && e.target.querySelector('wpc-welcome-popup');
    if (popup) popup.close();
  });
})();
